import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * DetailGallery — a small image carousel used inside the product detail
 * dialog. Renders arrow controls, dots, and a soft framer-motion crossfade.
 * Falls back to a single static image when only one slide is provided.
 */
export default function DetailGallery({
  slides,
  imageFit = "cover",
  imageBg,
  tagline,
  badge,
  testId,
}) {
  const items = (slides || []).filter(Boolean);
  const [i, setI] = useState(0);
  const [direction, setDirection] = useState(1);

  const count = items.length;
  useEffect(() => {
    if (i > count - 1) setI(0);
  }, [count, i]);

  if (count === 0) return null;

  const go = (next) => {
    setDirection(next > i ? 1 : -1);
    setI((next + count) % count);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") go(i + 1);
    if (e.key === "ArrowLeft") go(i - 1);
  };

  const slide = items[i];

  return (
    <div
      className="relative aspect-[4/5] w-full flex-shrink-0 md:sticky md:top-0 bg-[#F7EFEA] overflow-hidden group"
      style={imageBg ? { backgroundColor: imageBg } : undefined}
      role="region"
      aria-label="Product image gallery"
      aria-roledescription="carousel"
      tabIndex={0}
      onKeyDown={onKeyDown}
      data-testid={testId}
    >
      <AnimatePresence initial={false} mode="popLayout" custom={direction}>
        <motion.img
          key={slide.src + i}
          src={slide.src}
          alt={slide.alt || ""}
          className={
            "absolute inset-0 w-full h-full " +
            (imageFit === "contain"
              ? "object-contain p-6"
              : "object-contain p-4 sm:p-6")
          }
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.99 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          draggable={false}
        />
      </AnimatePresence>

      {(tagline || badge) && (
        <div className="absolute top-4 left-4 flex flex-col items-start gap-2 z-10">
          {tagline && (
            <span className="bg-[#FFFDF9]/95 backdrop-blur rounded-full px-3 py-1 text-[10px] tracking-[0.28em] uppercase text-[#36292A]">
              {tagline}
            </span>
          )}
          {badge && (
            <span className="bg-[#36292A] text-white rounded-full px-3 py-1 text-[10px] tracking-[0.28em] uppercase">
              {badge}
            </span>
          )}
        </div>
      )}

      {count > 1 && (
        <>
          {/* Prev / Next arrows */}
          <button
            type="button"
            aria-label="Previous image"
            onClick={(e) => {
              e.stopPropagation();
              go(i - 1);
            }}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-[#FFFDF9]/85 backdrop-blur border border-white/70 text-[#36292A] hover:bg-[#FFFDF9] transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C48B9F] shadow-[0_6px_20px_-8px_rgba(54,41,42,0.35)] flex items-center justify-center"
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label="Next image"
            onClick={(e) => {
              e.stopPropagation();
              go(i + 1);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-[#FFFDF9]/85 backdrop-blur border border-white/70 text-[#36292A] hover:bg-[#FFFDF9] transition-all opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C48B9F] shadow-[0_6px_20px_-8px_rgba(54,41,42,0.35)] flex items-center justify-center"
            data-testid={`${testId}-next`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* Dot indicators */}
          <div
            className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 rounded-full bg-[#FFFDF9]/85 backdrop-blur border border-white/70 px-2.5 py-1.5 shadow-[0_6px_20px_-8px_rgba(54,41,42,0.35)]"
            data-testid={`${testId}-dots`}
          >
            {items.map((_, idx) => (
              <button
                key={idx}
                type="button"
                aria-label={`Go to image ${idx + 1}`}
                aria-current={idx === i ? "true" : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  go(idx);
                }}
                className={
                  "rounded-full transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C48B9F] " +
                  (idx === i
                    ? "w-4 h-1.5 bg-[#36292A]"
                    : "w-1.5 h-1.5 bg-[#36292A]/30 hover:bg-[#36292A]/50")
                }
                data-testid={`${testId}-dot-${idx}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
